import type { SetPause, SetPausePhase } from "@heraion/shared";
import type { TFn } from "./i18nTypes";

// HI-8: the words of a pause inside the rep, one place for the logger's line,
// the editor's chips and the exercise sheet, so the three never name a phase
// differently (interval-training.md §10.6: three names, each explained).

type Words = { key: string; fallback: string };

// Records keyed by the phase: a fourth phase is a type error here.
const NAME: Record<SetPausePhase, Words> = {
  eccentric: { key: "program.pause.phase.eccentric", fallback: "Eccentric" },
  concentric: { key: "program.pause.phase.concentric", fallback: "Concentric" },
  isometric: { key: "program.pause.phase.isometric", fallback: "Isometric" },
};

const MEANING: Record<SetPausePhase, Words> = {
  eccentric: {
    key: "program.pause.explain.eccentric",
    fallback: "Lowering the weight. The muscle works while it gets longer. Go down slowly, for the seconds shown.",
  },
  concentric: {
    key: "program.pause.explain.concentric",
    fallback: "Lifting the weight. The muscle works while it gets shorter. Go up slowly, for the seconds shown.",
  },
  isometric: {
    key: "program.pause.explain.isometric",
    fallback: "Holding still. The muscle works without moving. Stop for the seconds shown, usually at the bottom.",
  },
};

const LABEL: Record<SetPausePhase, Words> = {
  eccentric: { key: "program.pause.label.eccentric", fallback: "{{sec}} s lowering" },
  concentric: { key: "program.pause.label.concentric", fallback: "{{sec}} s lifting" },
  isometric: { key: "program.pause.label.isometric", fallback: "{{sec}} s hold" },
};

/** The phase's name: «Eccentric». */
export const pausePhaseName = (t: TFn, phase: SetPausePhase): string =>
  t(NAME[phase].key, { defaultValue: NAME[phase].fallback });

/** What the phase means, in plain words, without its name. */
export const pausePhaseMeaning = (t: TFn, phase: SetPausePhase): string =>
  t(MEANING[phase].key, { defaultValue: MEANING[phase].fallback });

/** The instruction as the athlete reads it: «3 s hold». */
export const pauseLabel = (t: TFn, pause: SetPause): string =>
  t(LABEL[pause.phase].key, { sec: pause.sec, defaultValue: LABEL[pause.phase].fallback });

/** The one-line tip: the name, then what it means. */
export const pauseTip = (t: TFn, phase: SetPausePhase): string =>
  t("program.pause.tip", {
    phase: pausePhaseName(t, phase),
    meaning: pausePhaseMeaning(t, phase),
    defaultValue: "{{phase}}: {{meaning}}",
  });
